import Generic from 'src/models/generic/Generic'
import InventoryItemAdjustment, { ADJUSTMENT_TYPE_OPTIONS } from 'src/models/inventory-item/InventoryItemAdjustment'
import InventorySupplier from 'src/models/inventory-supplier/InventorySupplier'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, IsoDateTime, JsonObject } from 'src/models/model'
import RegisterStation from 'src/models/register/RegisterStation'

export default class InventoryItemBulkAdjustment extends Model {
  static name = 'InventoryItemBulkAdjustment'

  static versioned = true

  static deletable = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Inventory]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      adjustmentType: {
        type: String,
        options: ADJUSTMENT_TYPE_OPTIONS.BULK
      },
      location: {
        type: Location,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      registerStation: {
        type: RegisterStation,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      transferLocationId: {
        type: ID,
        default: null
      },
      transferRegisterStationId: {
        type: ID,
        default: null
      },
      inventorySupplier: {
        type: InventorySupplier,
        default: null,
        encode: encoders.hasOne,
        filterable: true
      },
      customAttributes: {
        type: JsonObject,
        default: {}
      },
      createdAt: {
        type: IsoDateTime,
        output: true
      },
      effectiveAt: {
        type: IsoDateTime,
        default () {
          const now = new IsoDateTime()
          /**
           * Remove miliseconds from the current time. This is because we use
           * this value to cache inventory item balances, and the backend
           * doesn't save the milliseconds, so saving the bulk adjustment busts
           * the inventory item balances cache.
           */
          now.setSeconds(now.getSeconds(), 0)
          return now
        }
      },
      committedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      inventoryItemAdjustments: {
        type: [InventoryItemAdjustment],
        acceptsNestedAttributes: true,
        allowDestroy: true
      },
      action: {
        type: String,
        default: null,
        input: true
      },
      locationOrRegisterStation: {
        type: Object,
        default: null,
        local: true
      },
      siteTransfer: {
        type: Boolean,
        default: false,
        local: true
      },
      siteTransferChangedProgrammatically: {
        type: Boolean,
        default: false,
        local: true
      },
      transferLocationOrRegisterStation: {
        type: Object,
        default: null,
        local: true
      }
    }

    this.computed = {
      hasAdjustments () {
        // We rely on this to determine if the bulk adjustment is deletable, so
        // without the data, we assume it has adjustments for safety.
        return this.inventoryItemAdjustments?.data?.length !== 0
      },
      isPreparation () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.PREPARATION
      },
      isReceipt () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.RECEIPT
      },
      isStocktake () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE
      },
      isWaste () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.WASTE
      },
      isTransfer () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER
      },
      isTransferReceipt () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER_RECEIPT
      },
      isMisc () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.MISC
      },
      isCommitted () {
        return !!this.committedAt
      },
      hasUncommittedAdjustments () {
        return _(this.inventoryItemAdjustments.data)
          .filter(adjustment => !adjustment.isCommitted)
          .value()
          .length > 0
      },
      constructorData () {
        return {
          adjustmentType: this.adjustmentType,
          locationId: this.location?.id,
          registerStationId: this.registerStation?.id,
          transferLocationId: this.transferLocationId,
          transferRegisterStationId: this.transferRegisterStationId,
          action: this.action
        }
      },
      hasLocationOrRegisterStation () {
        return !!(
          this.location || this.registerStation
        )
      },
      locationOrRegisterStationDisplayValue () {
        return (
          this.location ||
          this.registerStation
        )?.inventoryStrategyEntityDisplayName
      },
      hasTransferLocationOrRegisterStation () {
        return !!(
          this.transferLocationId || this.transferRegisterStationId
        )
      },
      transferLocationOrRegisterStationDisplayValue () {
        return (
          _.find(this.configuration.currentCircuit.locations.data, ({ id }) => id === this.transferLocationId) ||
          _.find(this.configuration.registerStations.data, ({ id }) => id === this.transferRegisterStationId)
        )?.inventoryStrategyEntityDisplayName
      },
      committedAtSiteTime () {
        return this.configuration.currentSite.inSiteTime(this.committedAt)
      },
      effectiveAtSiteTime () {
        return this.configuration.currentSite.inSiteTime(this.effectiveAt)
      },
      showAdjustmentsTable () {
        return !!this.adjustmentType && this.hasLocationOrRegisterStation && (
          !this.isTransfer || this.hasTransferLocationOrRegisterStation
        )
      },
      showAdjustmentReason () {
        return (
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.DAMAGE ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.THEFT ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.WASTE ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.MISC
        )
      },
      adjustmentReasonRules () {
        if (this.isMisc) {
          return [
            val => (val !== '' && val !== null && val !== undefined) || 'Field is required',
            val => !val || val.length < 256 || 'Max length 255 characters'
          ]
        } else {
          return [val => !val || val.length < 256 || 'Max length 255 characters']
        }
      },
      unitQuantityRules () {
        if (this.isMisc) {
          return [val => (val !== '' && val !== null && val !== undefined && val !== 0) || 'Field is required']
        } else if (this.isStocktake) {
          return [val => (val !== '' && val !== null && val !== undefined && val >= 0) || 'Field is required']
        } else {
          return [val => (val !== '' && val !== null && val !== undefined && val > 0) || 'Field is required']
        }
      },
      readonly () {
        return !_.isNil(this.committedAt) || this.hasAdjustments
      },
      deletable () {
        return !this.readonly
      }
    }

    this.queries = {
      pendingInventoryItemBulkAdjustmentsCount: {
        arguments: {},
        results: {
          count: Generic
        }
      },
      pendingInventoryItemBulkAdjustmentForRegisterStation: {
        arguments: {
          registerStationId: {
            type: ID,
            required: true
          }
        },
        results: {
          inventoryItemBulkAdjustment: InventoryItemBulkAdjustment
        }
      }
    }

    this.watchers = [
      [
        function () { return this.locationOrRegisterStation },
        function () {
          if (this.locationOrRegisterStation?.typeName === 'Location') {
            this.location = this.locationOrRegisterStation
            this.registerStation = null
          } else if (this.locationOrRegisterStation?.typeName === 'RegisterStation') {
            this.registerStation = this.locationOrRegisterStation
            this.location = null
          } else {
            this.location = null
            this.registerStation = null
          }
        }
      ],
      [
        function () { return this.siteTransfer },
        function () {
          if (this.siteTransferChangedProgrammatically) {
            this.siteTransferChangedProgrammatically = false
            return
          }

          this.transferLocationOrRegisterStation = null
          this.transferLocationId = null
          this.transferRegisterStationId = null

          if (!this.siteTransfer) {
            return
          }

          if (this.registerStation || (this.location && this.location.id !== this.configuration.currentSite.activeInventoryStrategy?.defaultInventoryLocationId)) {
            /**
             * Only allow site transfers between default inventory locations
             */
            this.locationOrRegisterStation = null
            this.registerStation = null
          }
        }
      ],
      [
        function () { return this.transferLocationOrRegisterStation },
        function () {
          if (this.transferLocationOrRegisterStation?.typeName === 'Location') {
            this.transferLocationId = this.transferLocationOrRegisterStation.id
            this.transferRegisterStationId = null
          } else if (this.transferLocationOrRegisterStation?.typeName === 'RegisterStation') {
            this.transferRegisterStationId = this.transferLocationOrRegisterStation.id
            this.transferLocationId = null
          } else {
            this.transferLocationId = null
            this.transferRegisterStationId = null
          }
        }
      ],
      [
        function () { return this.transferLocationId },
        function () {
          const currentSiteLocationIds = _.map(this.configuration.locations.data, 'id')

          if (!this.siteTransfer && !_.isNil(this.transferLocationId) && !_.includes(currentSiteLocationIds, this.transferLocationId)) {
            this.siteTransferChangedProgrammatically = true
            this.siteTransfer = true
          }
        }
      ]
    ]

    this.paginate = true
    this.filterable = true
  }

  setCommittedAt (committedAtSiteTime) {
    this.committedAt = this.configuration.currentSite.inGMT(committedAtSiteTime)
  }

  setEffectiveAt (effectiveAtSiteTime) {
    this.effectiveAt = this.configuration.currentSite.inGMT(effectiveAtSiteTime)
  }
}
